'use client'

// React Imports
import type { ReactElement } from 'react'

// Next Imports
import Link from 'next/link'

// MUI Imports
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

// Hook Imports
import type { RecordSummaryWithLink } from '@/hooks/useRecordSummary'

type BamaRecordPreviewProps = {
  record: RecordSummaryWithLink
  children: ReactElement
}

// Espera antes de abrir, para no mostrar la tarjeta al pasar el cursor de largo.
const PREVIEW_ENTER_DELAY_MS = 400

// Margen para llevar el cursor a la tarjeta sin que se cierre.
const PREVIEW_LEAVE_DELAY_MS = 150
const PREVIEW_MAX_WIDTH_PX = 340
const PREVIEW_MIN_WIDTH_PX = 220

// Tarjeta al pasar el cursor sobre el código de un registro; el clic sigue yendo al detalle.
const BamaRecordPreview = ({ record, children }: BamaRecordPreviewProps) => {
  if (!record.details?.length) return children

  return (
    <Tooltip
      placement='bottom-start'
      enterDelay={PREVIEW_ENTER_DELAY_MS}
      leaveDelay={PREVIEW_LEAVE_DELAY_MS}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: 'background.paper',
            color: 'text.primary',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 6,
            maxWidth: PREVIEW_MAX_WIDTH_PX,
            p: 3
          }
        }
      }}
      title={
        <Box className='flex flex-col gap-2' sx={{ minInlineSize: PREVIEW_MIN_WIDTH_PX }}>
          <div>
            <Typography variant='subtitle2' className='font-medium'>
              {record.code || record.name}
            </Typography>
            {record.code && record.name && (
              <Typography variant='body2' color='text.secondary'>
                {record.name}
              </Typography>
            )}
          </div>
          <Divider />
          {record.details.map(detail => (
            <div key={detail.label} className='flex justify-between gap-4'>
              <Typography variant='caption' color='text.secondary'>
                {detail.label}
              </Typography>
              <Typography variant='caption' className='text-end'>
                {detail.value}
              </Typography>
            </div>
          ))}
          <Typography component={Link} href={record.href} variant='caption' color='primary' className='hover:underline'>
            Ver detalle
          </Typography>
        </Box>
      }
    >
      {children}
    </Tooltip>
  )
}

export default BamaRecordPreview
